
import React, { useState, useRef, useEffect } from "react";
import "./chatbot.css";
import ReactMarkdown from "react-markdown";

type Sender = "user" | "bot";

interface ChatMessage {
  text: string;
  sender: Sender;
}

interface HistoryMessage {
  role: "user" | "assistant";
  content: string;
}

interface AiResponse {
  response?: string;
  error?: string;
}

const API_URL =
  "https://2tacftlvzb.execute-api.eu-north-1.amazonaws.com/default/chatbotFunction";

const MAX_MESSAGE_LENGTH = 500;
const MAX_HISTORY_ITEMS = 6;

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // References
  const chatEndRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HistoryMessage[]>([]);
  const requestInProgress = useRef(false);

  // Open / close chatbot
  const toggleChatbot = () => {
    setIsOpen((previous) => !previous);
  };

  // Automatically scroll to newest message
  useEffect(() => {
    if (isOpen) {
      chatEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages, isLoading, error, isOpen]);

  // Handle sending a message
  const handleSendMessage = async () => {
    const userMessage = input.trim();

    if (!userMessage || requestInProgress.current) {
      return;
    }

    requestInProgress.current = true;
    setIsLoading(true);
    setError(null);

    // Capture history before sending the new message
    const previousHistory = historyRef.current.slice(
      -MAX_HISTORY_ITEMS
    );

    // Display user message immediately
    setMessages((previous) => [
      ...previous,
      {
        text: userMessage,
        sender: "user",
      },
    ]);

    setInput("");

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          message: userMessage,
          history: previousHistory,
        }),
      });

      const data: AiResponse = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || `Request failed: ${response.status}`
        );
      }

      const botResponse = data.response;

      if (
        typeof botResponse !== "string" ||
        !botResponse.trim()
      ) {
        throw new Error("Empty response from AI");
      }

      // Display the AI response
      setMessages((previous) => [
        ...previous,
        {
          text: botResponse,
          sender: "bot",
        },
      ]);

      // Save only successful conversation turns

 // Save only successful conversation turns
const updatedHistory: HistoryMessage[] = [
  ...previousHistory,
  {
    role: "user",
    content: userMessage,
  },
  {
    role: "assistant",
    content: botResponse,
  },
];

historyRef.current = updatedHistory.slice(-MAX_HISTORY_ITEMS);


    } catch (err) {
      console.error("Chatbot request failed:", err);

      setError(
        "Sorry, something went wrong. Please try again."
      );

      // Remove failed message and restore text for retry
      setMessages((previous) => previous.slice(0, -1));
      setInput(userMessage);

    } finally {
      setIsLoading(false);
      requestInProgress.current = false;
    }
  };

  return (
    <>
      {/* Chatbot launcher */}
      <div
        id="chatbot"
        role="button"
        tabIndex={0}
        aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
        aria-expanded={isOpen}
        onClick={toggleChatbot}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleChatbot();
          }
        }}
        className={isOpen ? "paused" : ""}
      />

      {/* Chat popup */}
      {isOpen && (
        <div
          className="chat-popup"
          style={{
            right: 0,
            bottom: 0,
          }}
        >
          <div className="chat-header">
            <h5>Talk to my AI assistant</h5>

            <button
              type="button"
              className="close-btn"
              aria-label="Close chat"
              onClick={toggleChatbot}
            >
              &times;
            </button>
          </div>

          <div
            className="chat-body"
            role="log"
            aria-live="polite"
          >
            {/* Welcome message */}
            {messages.length === 0 && (
              <div className="message bot">
                Hi! I'm Rehan's AI assistant.
                Feel free to ask about his experience,
                technical skills, or projects.
              </div>
            )}

            {/* Conversation messages */}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`message ${msg.sender}`}
              >
                {msg.sender === "bot" ? (
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  >
                    {msg.text}
                  </ReactMarkdown>
                ) : (
                  msg.text
                )}
              </div>
            ))}


            {/* Loading indicator */}
            {isLoading && (
              <div className="loading">
                Thinking...
              </div>
            )}

            {/* Error message */}
            {error && (
              <div
                className="chat-error"
                role="alert"
              >
                {error}
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Message input */}
          <form
            className="chat-footer"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSendMessage();
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about Rehan..."
              maxLength={MAX_MESSAGE_LENGTH}
              className="chat-input"
              aria-label="Message for Rehan's AI assistant"
              disabled={isLoading}
            />

            <button
              type="submit"
              disabled={isLoading || !input.trim()}
            >
              {isLoading ? "Sending..." : "Send"}
            </button>
          </form>

          <p className="char-counter">
            {input.length}/{MAX_MESSAGE_LENGTH}
          </p>
        </div>
      )}
    </>
  );
};

export default Chatbot;
